import dayjs from 'dayjs';
import {
  ActionIcon,
  Alert,
  Box,
  Button,
  Drawer,
  Flex,
  Group,
  LoadingOverlay,
  Radio,
  ScrollArea,
  Stack,
  Select,
  Text,
  TextInput,
} from '@mantine/core';
import { DatePickerInput, TimeInput } from '@mantine/dates';
import {
  Appointment,
  Location,
  Organization,
  Patient,
  Practitioner,
  PractitionerRole,
  Reference,
} from '@medplum/fhirtypes';
import { useEffect, useMemo, useState } from 'react';
import utc from 'dayjs/plugin/utc';
import tz from 'dayjs/plugin/timezone';
import { alert } from '@/services/alert';
import { notifications } from '@mantine/notifications';
import { IconX } from '@tabler/icons-react';
import { fetchBot } from '@/services/services';
import { getBrowserTimezone, normalizeUsTimezoneToCanonical } from '../utils/common';
import { createReference, formatCodeableConcept, formatHumanName, isResource } from '@medplum/core';
import { LocationInput } from '../components/LocationInput';
import { PractitionerInput } from '@/components/PractitionerInput';
import PatientPhoneSelector from '@/components/PatientPhoneSelector';
import { GuestsSelector, GuestItemComponent } from './GuestsSelector';
import { RichTextInput } from '@/components/RichTextInput/RichTextInput';
import { ResourceAvatar, ResourceInput, useMedplum } from '@medplum/react';
import { getCodeableConceptCode, getUUID } from '@bariendo/lib/services';
import {
  BookAppointmentRequest,
  BookAppointmentResult,
  BookAppointmentSeriesResult,
  Guest,
  CreateZoomMeetingRequest,
  CreateZoomMeetingResponse,
} from '@bariendo/lib/scheduling';
import {
  APPOINTMENT_SERIES_IDENTIFIER_SYSTEM,
  IV_THERAPY_APPOINTMENT_TYPE_CODE,
  ProviderRole,
} from '@bariendo/lib/constants';
import { getAllowedProviderRolesForActivityDefinition } from '@bariendo/lib/scheduling';
import { VIRTUAL_ENCOUNTER_CLASS_CODING, AMBULATORY_ENCOUNTER_CLASS_CODING } from '@bariendo/lib/codings';
import { useSchedulingPageContext } from '../SchedulingPageContext';
import { useUpcomingConsult } from './useUpcomingConsult';
import { REPEAT_TIMES_OPTIONS } from '../Block/blockDrawer.constants';
import {
  PractitionerWithRole,
  AppointmentFormData,
  AppointmentRecurrencePreset,
  AppointmentRepeatingInterval,
  GuestFormData,
} from '../types';
import { TimezoneInput } from '../components/TimezoneInput';
import { useLocationTimezone } from '../hooks/useLocationTimezone';
import { mapAppointmentToFormData } from '../utils/appointments';
import {
  buildRecurrenceSettingsFromForm,
  buildZoomRecurrenceFromForm,
  computeMonthlyNthWeekdayFromDate,
  DEFAULT_MONTHLY_RECURRENCE_WEEKDAY,
  nextFormDataWithRecurrencePreset,
  toOrdinal,
} from '../utils/appointment';

dayjs.extend(utc);
dayjs.extend(tz);

interface AppointmentDrawerProps {
  opened: boolean;
  onClose: () => void;
  practitioners?: PractitionerWithRole[];
  appointmentReference: string | null;
  initialGuests?: GuestFormData[];
}

function OrganizationSelect({ resource }: { resource: Organization }) {
  const address = resource.address?.[0] ?? {};
  return (
    <Group wrap="nowrap">
      <ResourceAvatar value={resource} />
      <Stack gap="xs">
        <Text fw={500}>{resource.name}</Text>
        <Text size="xs" c="dimmed">
          {address.text ?? 'No address found'}
        </Text>
      </Stack>
    </Group>
  );
}

const initialFormData: AppointmentFormData = {
  appointmentType: null,
  appointmentTypeCode: null,
  provider: null,
  partner: null,
  guests: [],
  visitType: VIRTUAL_ENCOUNTER_CLASS_CODING,
  location: null,
  timezone: normalizeUsTimezoneToCanonical(getBrowserTimezone()),
  callType: 'video',
  videoLinkType: 'zoom',
  videoLink: '',
  phoneNumber: '',
  date: null,
  startTime: '',
  notes: '',
  notesJson: null,
  appointmentReference: null,
  editScope: 'single',
  isRepeating: false,
  recurrencePreset: 'none',
  repeatingInterval: AppointmentRepeatingInterval.WEEKLY,
  repeatTimes: 1,
  monthlyNth: 1,
  monthlyWeekday: DEFAULT_MONTHLY_RECURRENCE_WEEKDAY,
};

export function AppointmentDrawer({
  opened,
  onClose,
  practitioners,
  appointmentReference,
  initialGuests,
}: AppointmentDrawerProps) {
  const medplum = useMedplum();
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingAppointment, setIsLoadingAppointment] = useState(false);
  const [isIvTherapySelected, setIsIvTherapySelected] = useState(false);
  const [editingSeriesId, setEditingSeriesId] = useState<string | undefined>(undefined);
  const [formData, setFormData] = useState<AppointmentFormData>(initialFormData);
  const selectedPatient = formData.guests.find((guest): guest is Patient => guest.resourceType === 'Patient');
  const upcomingConsult = useUpcomingConsult(
    selectedPatient,
    formData.appointmentTypeCode,
    opened && !appointmentReference
  );
  const [filteredPractitioners, setFilteredPractitioners] = useState<PractitionerWithRole[]>(practitioners ?? []);
  const { appointmentTypeCodings, getActivityDefinitionForAppointmentType } = useSchedulingPageContext();

  // The type and provider an edited appointment was loaded with. Its own provider stays
  // offered and selected while the type is still the one it was booked as, even where
  // that type would not offer them: an NP booked into a PN consultation as overflow
  // (BAR-3101). Any other type gets its own roles, so an edit cannot move an NP into a
  // PN consultation.
  const [loadedBooking, setLoadedBooking] = useState<{ typeCode: string | null; providerId?: string } | null>(null);
  const bookedProviderFor = (typeCode: string | null | undefined): string | undefined =>
    loadedBooking && typeCode === loadedBooking.typeCode ? loadedBooking.providerId : undefined;
  const assignedProviderId = bookedProviderFor(formData.appointmentTypeCode);

  // Keep filtered practitioners in sync when the list of practitioners changes externally.
  useEffect(() => {
    if (!formData.appointmentType) {
      setFilteredPractitioners(practitioners ?? []);
      return;
    }

    const allowedRoles = getAllowedProviderRolesForActivityDefinition(formData.appointmentType);
    if (allowedRoles.length === 0) {
      setFilteredPractitioners([]);
      return;
    }

    setFilteredPractitioners(
      (practitioners ?? []).filter(
        (p) => (p.roleCode && allowedRoles.includes(p.roleCode as any)) || p.id === assignedProviderId
      )
    );
  }, [practitioners, formData.appointmentType, assignedProviderId]);

  useEffect(() => {
    if (formData.recurrencePreset !== 'monthly' || !formData.date || !formData.timezone) {
      return;
    }
    const { n, weekday } = computeMonthlyNthWeekdayFromDate(formData.date, formData.timezone);
    setFormData((prev) => {
      if (prev.recurrencePreset !== 'monthly') {
        return prev;
      }
      if (prev.monthlyNth === n && prev.monthlyWeekday === weekday) {
        return prev;
      }
      return {
        ...prev,
        monthlyNth: n,
        monthlyWeekday: weekday,
        repeatingInterval: AppointmentRepeatingInterval.MONTHLY,
      };
    });
  }, [formData.date, formData.timezone, formData.recurrencePreset]);

  const {
    timezoneSuffix,
    resolveLocationChange,
    resolveTimezoneSelection,
    reset: resetTimezoneSuffix,
  } = useLocationTimezone();

  const handleTimezoneChange = (value: string) => {
    const canonical = normalizeUsTimezoneToCanonical(value);
    resolveTimezoneSelection(canonical);
    setFormData((prev) => ({ ...prev, timezone: canonical }));
  };

  useEffect(() => {
    const loadAppointment = async () => {
      if (!opened) return;
      if (!appointmentReference) {
        setFormData({
          ...initialFormData,
          guests: initialGuests ?? [],
        });
        setEditingSeriesId(undefined);
        setIsIvTherapySelected(false);
        setLoadedBooking(null);
        setIsLoadingAppointment(false);
        return;
      }
      setIsLoadingAppointment(true);
      try {
        const appointment = (await medplum.readReference({
          reference: appointmentReference,
        } as Reference<Appointment>)) as Appointment | undefined;
        if (!appointment) return;

        const mappedAppointment = await mapAppointmentToFormData(medplum, appointment);
        const canonicalTimezone = mappedAppointment.timezone
          ? normalizeUsTimezoneToCanonical(mappedAppointment.timezone)
          : null;
        setFormData({ ...mappedAppointment, timezone: canonicalTimezone });
        setLoadedBooking({
          typeCode: mappedAppointment.appointmentTypeCode,
          providerId: mappedAppointment.provider?.id,
        });
        setEditingSeriesId(
          appointment.identifier?.find((i) => i.system === APPOINTMENT_SERIES_IDENTIFIER_SYSTEM)?.value
        );
        setIsIvTherapySelected(
          (mappedAppointment.appointmentType?.code &&
            getCodeableConceptCode(mappedAppointment.appointmentType.code) === IV_THERAPY_APPOINTMENT_TYPE_CODE) ??
            false
        );
        resolveTimezoneSelection(canonicalTimezone ?? normalizeUsTimezoneToCanonical(getBrowserTimezone()));
      } finally {
        setIsLoadingAppointment(false);
      }
    };

    loadAppointment();
  }, [appointmentReference, initialGuests, medplum, opened, resolveTimezoneSelection]);

  /**
   * Reset the visit type for multiple guests
   * If the number of guests is greater than 1 and the call type is phone, set the call type to video and the phone number to empty
   * This is because we don't support phone calls for multiple guests
   */
  const resetVisitTypeForMultipleGuests = (guests: GuestFormData[], prevFormData: AppointmentFormData) => {
    let callType = prevFormData.callType;
    let phoneNumber = prevFormData.phoneNumber;

    if (guests.length > 1 && callType === 'phone') {
      callType = 'video';
      phoneNumber = '';
    }

    return { callType, phoneNumber };
  };

  const handleClose = () => {
    setFormData(initialFormData);
    resetTimezoneSuffix();
    onClose();
  };

  const handleAppointmentTypeChange = (code: string | null) => {
    const activityDefinition = getActivityDefinitionForAppointmentType(code ?? undefined) ?? null;
    const isIvTherapy = getCodeableConceptCode(activityDefinition?.code) === IV_THERAPY_APPOINTMENT_TYPE_CODE;

    // Filter practitioners based on allowed provider roles for the selected activity definition.
    if (activityDefinition) {
      const allowedRoles = getAllowedProviderRolesForActivityDefinition(activityDefinition);

      if (allowedRoles.length > 0) {
        const nextPractitioners =
          practitioners?.filter(
            (p) => !p.roleCode || allowedRoles.includes(p.roleCode as any) || p.id === bookedProviderFor(code)
          ) ?? [];
        setFilteredPractitioners(nextPractitioners);
      } else {
        setFilteredPractitioners(practitioners ?? []);
      }
    } else {
      setFilteredPractitioners(practitioners ?? []);
    }

    setFormData((prev) => {
      const nextProvider = (() => {
        if (isIvTherapy) {
          return null;
        }

        if (!activityDefinition || !prev.provider) {
          return prev.provider;
        }

        // Back on the type it was booked as, with the provider it was booked with.
        if (prev.provider.id === bookedProviderFor(code)) {
          return prev.provider;
        }

        const allowedRoles = getAllowedProviderRolesForActivityDefinition(activityDefinition);
        if (allowedRoles.length === 0) {
          return prev.provider;
        }

        // Resolve role from practitioners list (provider from loaded appointment may not have roleCode)
        const providerWithRole = practitioners?.find((p) => p.id === prev.provider?.id);
        const providerRoleCode = (providerWithRole?.roleCode ?? prev.provider.roleCode) as ProviderRole | undefined;
        if (providerRoleCode && allowedRoles.includes(providerRoleCode)) {
          return prev.provider;
        }

        return null;
      })();

      return {
        ...prev,
        appointmentType: activityDefinition,
        appointmentTypeCode: code,
        callType: isIvTherapy ? null : 'video',
        partner: isIvTherapy ? prev.partner : null,
        provider: nextProvider,
        visitType: isIvTherapy ? AMBULATORY_ENCOUNTER_CLASS_CODING : VIRTUAL_ENCOUNTER_CLASS_CODING,
        ...(isIvTherapy ? { isRepeating: false, recurrencePreset: 'none' as const } : {}),
      };
    });

    setIsIvTherapySelected(isIvTherapy);
  };

  const handleProviderChange = (resource: Practitioner | null) => {
    setFormData((prev) => ({ ...prev, provider: resource ?? null }));
  };

  const handleGuestChange = (resources: GuestFormData[] | undefined) => {
    setFormData((prev) => {
      const newGuests = resources ?? [];
      const { callType, phoneNumber } = resetVisitTypeForMultipleGuests(newGuests, prev);
      return { ...prev, guests: newGuests, callType, phoneNumber };
    });
  };

  // Fields are disabled until appointment type is selected
  const isAppointmentTypeSelected = formData.appointmentType !== null;

  const isInPersonVisit = formData.visitType.code === AMBULATORY_ENCOUNTER_CLASS_CODING.code;
  const hasLocationForInPersonVisit = isInPersonVisit && !isIvTherapySelected ? formData.location !== null : true;

  // Validation: check if all required fields are filled
  // Location is required for in-person visits
  const recurrenceValid =
    !(isIvTherapySelected && formData.isRepeating) &&
    (!formData.isRepeating || !!appointmentReference || (formData.repeatTimes >= 1 && formData.repeatTimes <= 52));

  const isFormValid =
    formData.appointmentType !== null &&
    (isIvTherapySelected ? formData.partner !== null : formData.provider !== null) &&
    formData.date !== null &&
    formData.visitType !== null &&
    formData.startTime !== '' &&
    hasLocationForInPersonVisit &&
    recurrenceValid &&
    (formData.visitType.code === VIRTUAL_ENCOUNTER_CLASS_CODING.code
      ? formData.callType === 'phone'
        ? formData.guests.length > 0
        : true
      : true);

  // Get duration from the selected appointment type
  const duration = formData.appointmentType?.timingDuration;
  const durationDisplay = duration ? `${duration.value} ${duration.unit}` : '';

  const recurrenceSelectData = useMemo(() => {
    const tz = formData.timezone ?? 'UTC';
    const base = [
      { value: 'none', label: 'Does not repeat' },
      { value: 'daily', label: 'Daily' },
      { value: 'weekly', label: 'Weekly' },
      { value: 'biweekly', label: 'Every 2 weeks' },
    ];
    if (!formData.date) {
      return [...base, { value: 'monthly', label: 'Monthly on the… (select a date)', disabled: true }];
    }
    const { n } = computeMonthlyNthWeekdayFromDate(formData.date, tz);
    const weekdayName = dayjs(formData.date).tz(tz).format('dddd');
    return [...base, { value: 'monthly', label: `Monthly on the ${toOrdinal(n)} ${weekdayName}` }];
  }, [formData.date, formData.timezone]);

  const handleRecurrencePresetChange = (value: string | null) => {
    if (!value) {
      return;
    }
    const tz = formData.timezone ?? 'UTC';
    setFormData((prev) => nextFormDataWithRecurrencePreset(prev, value as AppointmentRecurrencePreset, tz));
  };

  const handleRepeatTimesChange = (value: string | null) => {
    if (!value) {
      return;
    }
    setFormData((prev) => ({ ...prev, repeatTimes: Number(value) }));
  };

  const getGuestsReferences = () => {
    return formData.guests.map((guest) => {
      if (isResource(guest, 'Patient')) {
        return createReference(guest as Patient);
      }

      return (guest as PractitionerRole).practitioner;
    });
  };

  const generateZoomMeeting = async (payload: CreateZoomMeetingRequest) => {
    try {
      const response = await fetchBot<CreateZoomMeetingResponse>(medplum, 'create-zoom-meeting', payload);
      return response;
    } catch (error) {
      notifications.show({
        title: error instanceof Error ? error.message : 'Unable to generate Zoom meeting link',
        message: 'Please insert a custom link or contact an Orbit admin to verify the Everself Zoom account.',
        color: 'red',
        autoClose: 10000,
      });

      setIsSaving(false);
      throw error;
    }
  };

  const getUTCAppointmentDate = () => {
    const appointmentDate = new Date(formData.date!);
    appointmentDate.setHours(Number(formData.startTime.split(':')[0]), Number(formData.startTime.split(':')[1]));
    return appointmentDate.toISOString();
  };

  const handleSave = async () => {
    if (isSaving || !isFormValid) return;

    if (
      !formData.appointmentType ||
      (isIvTherapySelected ? !formData.partner : !formData.provider) ||
      !formData.date ||
      !formData.timezone
    ) {
      alert.error('Please fill in all required fields');
      return;
    }

    setIsSaving(true);

    let zoomAdminUrl: string | undefined;

    if (formData.visitType.code === VIRTUAL_ENCOUNTER_CLASS_CODING.code && formData.videoLinkType === 'zoom') {
      const appointmentTypeName = formatCodeableConcept(formData.appointmentType?.code);

      // Send the start date in UTC timezone so the Zoom meeting is displayed in the correct timezone to the host.
      const start = getUTCAppointmentDate();
      const zoomRecurrence = buildZoomRecurrenceFromForm(formData);

      const zoomMeetingPayload: CreateZoomMeetingRequest = {
        start,
        duration: formData.appointmentType?.timingDuration?.value ?? 60,
        meetingTitle: `Everself - ${appointmentTypeName ?? 'Scheduled meeting'}`,
        timezone: 'UTC',
        ...(zoomRecurrence ? { recurrence: zoomRecurrence } : {}),
      };

      const zoomMeeting = await generateZoomMeeting(zoomMeetingPayload);
      formData.videoLink = zoomMeeting.joinUrl;
      zoomAdminUrl = zoomMeeting.startUrl;
    }

    try {
      const date = dayjs(formData.date).format('YYYY-MM-DD');
      const startTime = formData.startTime;
      const guests = getGuestsReferences();

      const recurrenceSettings = buildRecurrenceSettingsFromForm(formData);
      const seriesId = formData.isRepeating && !appointmentReference && recurrenceSettings ? getUUID() : undefined;

      const bookAppointmentPayload: BookAppointmentRequest = {
        activityDefinition: createReference(formData.appointmentType),
        practitioner: formData.provider ? createReference(formData.provider) : null,
        partner: formData.partner ? createReference(formData.partner) : null,
        guests: guests as Reference<Guest>[],
        date,
        startTime,
        timezone: formData.timezone,
        visitType: formData.visitType,
        location: formData.location ? createReference(formData.location) : undefined,
        notes: formData.notes || undefined,
        richTextNotes: formData.notesJson ? JSON.stringify(formData.notesJson) : undefined,
        ...(appointmentReference && {
          appointment: { reference: appointmentReference },
          ...(editingSeriesId ? { editScope: formData.editScope ?? 'single' } : {}),
        }),
        ...(seriesId && recurrenceSettings ? { seriesId, recurrence: recurrenceSettings } : {}),
      };

      if (!isInPersonVisit && formData.callType) {
        bookAppointmentPayload.customInfo = {
          virtualServiceType: formData.callType,
          virtualServiceUrl: formData.callType === 'video' ? formData.videoLink : undefined,
          virtualServiceAdminUrl:
            formData.callType === 'video' && formData.videoLinkType === 'zoom' ? zoomAdminUrl : undefined,
          virtualServicePhone: formData.callType === 'phone' ? formData.phoneNumber : undefined,
        };
      }

      const result = await fetchBot<BookAppointmentResult | BookAppointmentSeriesResult>(
        medplum,
        'book-appointment',
        bookAppointmentPayload
      );

      if ('results' in result && Array.isArray(result.results)) {
        if (result.warningDetails?.length) {
          const summary = result.warningDetails
            .map((d) => `${d.message} (occurrences: ${d.occurrenceIndexes.join(', ')})`)
            .join('\n');
          alert.warning(summary);
        } else if (result.warnings?.length) {
          alert.warning(result.warnings.map((w) => w.message).join('\n'));
        }
      } else if ('warnings' in result && result.warnings?.length) {
        for (const warning of result.warnings) {
          alert.warning(warning.message);
        }
      }

      handleClose();
    } catch (error) {
      alert.error(`Couldn't book appointment: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleVisitTypeChange = (value: string) => {
    const isVirtual = value === VIRTUAL_ENCOUNTER_CLASS_CODING.code;
    setFormData((prev) => ({
      ...prev,
      visitType: isVirtual ? VIRTUAL_ENCOUNTER_CLASS_CODING : AMBULATORY_ENCOUNTER_CLASS_CODING,
      location: isVirtual ? null : prev.location,
      timezone: null,
    }));
  };

  const handleLocationChange = (newLocation: Location | null) => {
    const { fallbackTimezone } = resolveLocationChange(newLocation);
    setFormData((prev) => ({
      ...prev,
      location: newLocation,
      timezone: normalizeUsTimezoneToCanonical(fallbackTimezone),
    }));
  };

  const appointmentTypeOptions =
    appointmentTypeCodings?.map((coding) => ({
      value: coding.code!,
      label: coding.display as string,
    })) ?? [];

  const renderVisitTypeSection = () => {
    const renderVideoTypeOptions = () => {
      if (formData.callType !== 'video') {
        return null;
      }

      return (
        <Stack>
          <Radio.Group
            disabled={!isAppointmentTypeSelected}
            value={formData.videoLinkType}
            onChange={(value) => setFormData((prev) => ({ ...prev, videoLinkType: value as 'zoom' | 'custom' }))}
          >
            <Flex direction="column" gap="xs">
              <Radio value="zoom" label="Generate meeting link" color="#228BE6" />
              <Radio value="custom" label="Custom link" color="#228BE6" />
            </Flex>
          </Radio.Group>

          {formData.videoLinkType === 'custom' && (
            <TextInput
              value={formData.videoLink}
              onChange={(event) => setFormData((prev) => ({ ...prev, videoLink: event.currentTarget?.value ?? '' }))}
              placeholder="Insert call link"
              disabled={!isAppointmentTypeSelected}
            />
          )}
        </Stack>
      );
    };

    return (
      <>
        <Box>
          <Text size="sm" fw={500} mb={4}>
            Visit type
            <Text component="span" c="red">
              *
            </Text>
          </Text>
          <Radio.Group
            value={formData.visitType.code}
            onChange={handleVisitTypeChange}
            disabled={!isAppointmentTypeSelected}
          >
            <Group>
              <Radio value={VIRTUAL_ENCOUNTER_CLASS_CODING.code} label="Virtual" color="#228BE6" />
              <Radio value={AMBULATORY_ENCOUNTER_CLASS_CODING.code} label="In-person" color="#228BE6" />
            </Group>
          </Radio.Group>
        </Box>

        {isInPersonVisit && (
          <Box>
            <LocationInput
              value={formData.location}
              onChange={handleLocationChange}
              disabled={!isAppointmentTypeSelected}
            />
          </Box>
        )}

        {!isInPersonVisit && (
          <Box>
            <Select
              mb="sm"
              data={[
                { value: 'video', label: 'Video call' },
                { value: 'phone', label: 'Phone call', disabled: formData.guests.length > 1 },
              ]}
              value={formData.callType}
              defaultValue="video"
              onChange={(value) => setFormData((prev) => ({ ...prev, callType: value as 'video' | 'phone' | null }))}
              disabled={!isAppointmentTypeSelected}
            />

            {renderVideoTypeOptions()}

            {formData.callType === 'phone' && (
              <PatientPhoneSelector
                patient={formData.guests[0] as Patient}
                value={formData.phoneNumber}
                onChange={(value) => setFormData((prev) => ({ ...prev, phoneNumber: value ?? '' }))}
                disabled={!isAppointmentTypeSelected}
              />
            )}
          </Box>
        )}
      </>
    );
  };

  return (
    <Drawer
      opened={opened}
      onClose={handleClose}
      position="right"
      size="sm"
      padding="md"
      styles={{
        header: { padding: '0 16px' },
        body: { padding: 0, height: '100vh', display: 'flex', flexDirection: 'column' },
        content: { display: 'flex', flexDirection: 'column' },
      }}
      withCloseButton={false}
    >
      <Drawer.Header>
        <Group justify="space-between" w="100%" align="center">
          <Text fw={600} size="lg">
            Appointment
          </Text>
          <ActionIcon variant="subtle" color="gray" size="sm" onClick={handleClose}>
            <IconX size={18} />
          </ActionIcon>
        </Group>
      </Drawer.Header>

      <Flex direction="column" style={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
        <LoadingOverlay visible={isLoadingAppointment} zIndex={10} overlayProps={{ blur: 2 }} />
        <ScrollArea style={{ flex: 1 }} px="md" pt="md" type="auto" offsetScrollbars>
          <Stack gap="md">
            <Box>
              <Select
                data={appointmentTypeOptions}
                value={formData.appointmentTypeCode}
                onChange={handleAppointmentTypeChange}
                placeholder="Select type"
                label="Appointment type"
                searchable
                required
              />
            </Box>

            <Box>
              {isIvTherapySelected ? (
                <Stack gap="xs">
                  <ResourceInput
                    name="partner"
                    label="Partner"
                    defaultValue={formData.partner ? createReference(formData.partner) : undefined}
                    resourceType="Organization"
                    placeholder="Select partner"
                    required={isIvTherapySelected}
                    onChange={(resource: Organization | undefined) =>
                      setFormData((prev) => ({ ...prev, partner: resource ?? null }))
                    }
                    itemComponent={(props: { resource: Organization }) => (
                      <OrganizationSelect resource={props.resource} />
                    )}
                    // TODO: Add search criteria for the partner.
                    // It should get only partners that have IV Therapy in their services
                  />
                  {formData.partner && (
                    <TextInput
                      readOnly
                      label="Address"
                      variant="unstyled"
                      value={formData.partner.address?.[0]?.text ?? 'No address found'}
                    />
                  )}
                </Stack>
              ) : (
                <PractitionerInput
                  value={formData.provider}
                  onChange={handleProviderChange}
                  disabled={!isAppointmentTypeSelected}
                  practitioners={filteredPractitioners}
                />
              )}
            </Box>

            <Box>
              {isIvTherapySelected ? (
                <ResourceInput
                  name="guest"
                  label="Guest"
                  required={true}
                  resourceType="Patient"
                  placeholder="Select guest"
                  disabled={!isAppointmentTypeSelected}
                  defaultValue={formData.guests[0] ? createReference(formData.guests[0] as Patient) : undefined}
                  itemComponent={(props: { resource: Patient }) => <GuestItemComponent resource={props.resource} />}
                  onChange={(resource: Patient | undefined) =>
                    setFormData((prev) => ({ ...prev, guests: resource ? [resource] : [] }))
                  }
                />
              ) : (
                <GuestsSelector
                  provider={formData.provider}
                  onChange={handleGuestChange}
                  disabled={!isAppointmentTypeSelected}
                  defaultValue={formData.guests}
                />
              )}
            </Box>

            {isIvTherapySelected ? null : renderVisitTypeSection()}

            <TimezoneInput
              value={formData.timezone ?? ''}
              onChange={handleTimezoneChange}
              displaySuffix={timezoneSuffix}
              disabled={isInPersonVisit || !isAppointmentTypeSelected}
            />

            <Box>
              <Text size="md" fw={500} c="gray.7" mb="xs">
                Date and time
              </Text>

              {appointmentReference && editingSeriesId && (
                <Select
                  mb="sm"
                  label="Apply changes to"
                  data={[
                    { value: 'single', label: 'This occurrence only' },
                    { value: 'thisAndFollowing', label: 'This and following' },
                    { value: 'all', label: 'All occurrences' },
                  ]}
                  value={formData.editScope ?? 'single'}
                  onChange={(value) =>
                    value && setFormData((prev) => ({ ...prev, editScope: value as AppointmentFormData['editScope'] }))
                  }
                  disabled={!isAppointmentTypeSelected}
                />
              )}

              <Box mb="sm">
                <DatePickerInput
                  required={true}
                  label="Date"
                  placeholder="Select date"
                  value={formData.date}
                  onChange={(value) =>
                    setFormData((prev) => {
                      const nextDate = value ? dayjs(value).toDate() : null;
                      if (!nextDate && prev.recurrencePreset === 'monthly') {
                        return {
                          ...prev,
                          date: null,
                          recurrencePreset: 'none',
                          isRepeating: false,
                          repeatingInterval: AppointmentRepeatingInterval.WEEKLY,
                        };
                      }
                      return { ...prev, date: nextDate };
                    })
                  }
                  valueFormat="MMMM D, YYYY"
                  minDate={new Date()}
                  disabled={!isAppointmentTypeSelected || (!!editingSeriesId && formData.editScope !== 'single')}
                />
              </Box>

              <Group grow>
                <Box>
                  <TimeInput
                    required={true}
                    label="Start time"
                    value={formData.startTime}
                    onChange={(event) =>
                      setFormData((prev) => ({ ...prev, startTime: event.currentTarget?.value ?? '' }))
                    }
                    disabled={!isAppointmentTypeSelected}
                  />
                </Box>
                <Box>
                  <TextInput
                    label="Duration"
                    value={durationDisplay}
                    readOnly
                    styles={{
                      input: {
                        backgroundColor: '#f8f9fa',
                        cursor: 'default',
                      },
                    }}
                  />
                </Box>
              </Group>

              {!appointmentReference && !isIvTherapySelected && (
                <>
                  <Select
                    mt="sm"
                    label="Recurrence"
                    data={recurrenceSelectData}
                    value={formData.recurrencePreset}
                    onChange={handleRecurrencePresetChange}
                    disabled={!isAppointmentTypeSelected}
                  />
                  <Select
                    mt="sm"
                    label="Ends after (occurrences)"
                    data={[...REPEAT_TIMES_OPTIONS]}
                    value={String(formData.repeatTimes)}
                    onChange={handleRepeatTimesChange}
                    disabled={!isAppointmentTypeSelected || formData.recurrencePreset === 'none'}
                  />
                </>
              )}
            </Box>

            <Box pb="md">
              <Text size="md" fw={500} c="gray.7" mb="xs">
                Notes
              </Text>
              <RichTextInput
                value={formData.notesJson ?? ''}
                onChange={(text, json) => setFormData((prev) => ({ ...prev, notes: text, notesJson: json }))}
                placeholder="Write Appointment Notes"
                contentMinHeight="100px"
                contentMaxHeight="200px"
                disabled={!isAppointmentTypeSelected}
              />
            </Box>
          </Stack>
        </ScrollArea>

        <Box
          px="md"
          py="sm"
          style={{
            borderTop: '1px solid var(--mantine-color-gray-3)',
            backgroundColor: 'var(--mantine-color-body)',
          }}
        >
          {upcomingConsult && (
            <Alert color="yellow" variant="light" mb="sm">
              {formatHumanName(selectedPatient?.name?.[0]) || 'This patient'} already has a consult on{' '}
              {dayjs(upcomingConsult.start).format('MMM D')}. Booking another will double-book them.
            </Alert>
          )}
          <Group justify="flex-end">
            <Button
              radius="xl"
              color="#228BE6"
              disabled={!isFormValid || isSaving}
              loading={isSaving}
              onClick={handleSave}
            >
              Save
            </Button>
          </Group>
        </Box>
      </Flex>
    </Drawer>
  );
}
